import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { AuthenticationService } from '../../../core/services/auth.service';
import { SpinnerService } from '../../../core/services/spinner.service';
import { noWhitespaceValidator } from '../../../app-const';
import { CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { PasswordInputComponent } from '../../../shared/components/password-input/password-input.component';
import { NzControlErrorTempComponent } from '../../../shared/components/nz-control-error-temp/nz-control-error-temp.component';
import { IsRequiredPipe } from '../../../shared/pipes/is-required.pipe';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    NzFormModule,
    NzInputModule,
    NzCheckboxModule,
    CarouselModule,
    PasswordInputComponent,
    NzControlErrorTempComponent,
    IsRequiredPipe
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  logo = '/assets/images/logo.svg';
  logoLight = '/assets/images/logo.svg';

  loginForm = new FormGroup({
    userName: new FormControl('', [Validators.required, noWhitespaceValidator]),
    password: new FormControl('', [Validators.required]),
    rememberMe: new FormControl(false),
  });

  loading = false;
  /** Set only by a ?returnUrl link; otherwise the user's permission-resolved landing route is used. */
  returnUrl: string | null = null;
  errorMessage = '';

  features = [
    {
      icon: 'isax isax-buildings',
      title: 'LOGIN.FEATURE_1_TITLE',
      desc: 'LOGIN.FEATURE_1_DESC'
    },
    {
      icon: 'isax isax-security-user',
      title: 'LOGIN.FEATURE_2_TITLE',
      desc: 'LOGIN.FEATURE_2_DESC'
    },
    {
      icon: 'isax isax-chart-2',
      title: 'LOGIN.FEATURE_3_TITLE',
      desc: 'LOGIN.FEATURE_3_DESC'
    }
  ];

  carouselOptions: OwlOptions = {
    loop: true,
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    dots: true,
    navSpeed: 800,
    autoplay: true,
    autoplayTimeout: 3500,
    autoplayHoverPause: true,
    rtl: true,
    responsive: {
      0: {
        items: 1,
      },
    },
    nav: false,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private spinner: SpinnerService,
    private auth: AuthenticationService,
    private translate: TranslateService
  ) {}

  private targetUrl(user: { landing_route?: string | null } | null): string {
    return this.returnUrl || user?.landing_route || '/home';
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['returnUrl']) {
        this.returnUrl = params['returnUrl'];
      }
    });

    // Check if already authenticated as super_admin
    if (this.auth._token) {
      this.auth.identity().subscribe((user) => {
        if (user && user.platform_role === 'super_admin') {
          this.router.navigateByUrl(this.targetUrl(user));
        }
      });
    }
  }

  get f() {
    return this.loginForm.controls;
  }

  mfaForm = new FormGroup({
    code: new FormControl('', [Validators.required]),
    recoveryCode: new FormControl('', [Validators.required]),
  });

  useRecoveryCode = false;
  mfaRequired = false;
  challengeToken = '';
  countdownSeconds = 300;
  countdownTimer: any = null;

  get formattedCountdown(): string {
    const mins = Math.floor(this.countdownSeconds / 60);
    const secs = this.countdownSeconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }

  startMfaCountdown(seconds: number): void {
    this.countdownSeconds = seconds;
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    this.countdownTimer = setInterval(() => {
      this.countdownSeconds--;
      if (this.countdownSeconds <= 0) {
        clearInterval(this.countdownTimer);
        const isAr = !this.translate?.currentLang || this.translate.currentLang === 'ar';
        this.errorMessage = isAr
          ? 'انتهت صلاحية جلسة التحقق. يرجى تسجيل الدخول مرة أخرى.'
          : 'MFA challenge expired. Please log in again.';
      }
    }, 1000);
  }

  toggleRecoveryCode(): void {
    this.useRecoveryCode = !this.useRecoveryCode;
    this.errorMessage = '';
  }

  cancelMfa(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    this.mfaRequired = false;
    this.challengeToken = '';
    this.useRecoveryCode = false;
    this.errorMessage = '';
    this.mfaForm.reset();
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      Object.values(this.loginForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.markAsTouched();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    const { userName, password, rememberMe } = this.loginForm.value;
    const isAr = !this.translate?.currentLang || this.translate.currentLang === 'ar';

    this.auth.login(userName!, password!, !!rememberMe).subscribe({
      next: (res) => {
        if (res && res.data?.mfa_required) {
          this.loading = false;
          this.mfaRequired = true;
          this.challengeToken = res.data.challenge_token;
          this.startMfaCountdown(res.data.expires_in || 300);
          return;
        }

        this.auth.identity(true).subscribe({
          next: (user) => {
            this.loading = false;
            if (user && user.platform_role === 'super_admin') {
              this.router.navigateByUrl(this.targetUrl(user));
            } else {
              this.errorMessage = isAr
                ? 'هذا الحساب لا يملك صلاحيات الدخول كمسؤول للمنصة.'
                : 'This account does not have platform administrator access.';
              this.auth.clearAuthenticationState(true);
            }
          },
          error: (err) => {
            this.loading = false;
            this.errorMessage = err?.error?.message || (isAr ? 'فشل التحقق من صلاحيات الحساب.' : 'Failed to verify account permissions.');
          }
        });
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 429) {
          const retryAfter = err?.error?.retry_after || 60;
          this.errorMessage = isAr
            ? `تم تجاوز عدد محاولات الدخول المسموح بها. يرجى المحاولة بعد ${retryAfter} ثانية.`
            : `Too many login attempts. Please try again in ${retryAfter} seconds.`;
        } else if (err?.status === 403 && err?.error?.error_code === 'ACCOUNT_INACTIVE') {
          this.errorMessage = isAr ? 'هذا الحساب معطل حالياً.' : 'This account is currently inactive.';
        } else {
          this.errorMessage = err?.error?.message || (isAr ? 'بيانات الدخول غير صحيحة.' : 'Invalid credentials.');
        }
      }
    });
  }

  onMfaSubmit(): void {
    if (this.countdownSeconds <= 0) {
      const isAr = !this.translate?.currentLang || this.translate.currentLang === 'ar';
      this.errorMessage = isAr
        ? 'انتهت صلاحية جلسة التحقق. يرجى تسجيل الدخول مرة أخرى.'
        : 'MFA challenge expired. Please log in again.';
      return;
    }

    const { code, recoveryCode } = this.mfaForm.value;
    const isAr = !this.translate?.currentLang || this.translate.currentLang === 'ar';

    if (!this.useRecoveryCode && (!code || code.trim().length === 0)) {
      this.errorMessage = isAr
        ? 'يرجى إدخال رمز التحقق المكون من 6 أرقام.'
        : 'Please enter the 6-digit authenticator code.';
      return;
    }

    if (this.useRecoveryCode && (!recoveryCode || recoveryCode.trim().length === 0)) {
      this.errorMessage = isAr
        ? 'يرجى إدخال رمز الاسترداد.'
        : 'Please enter a recovery code.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    const { rememberMe } = this.loginForm.value;

    this.auth.verifyMfaChallenge(
      this.challengeToken,
      this.useRecoveryCode ? undefined : code?.trim(),
      this.useRecoveryCode ? recoveryCode?.trim() : undefined,
      !!rememberMe
    ).subscribe({
      next: () => {
        if (this.countdownTimer) {
          clearInterval(this.countdownTimer);
        }
        this.auth.identity(true).subscribe({
          next: (user) => {
            this.loading = false;
            if (user && user.platform_role === 'super_admin') {
              this.router.navigateByUrl(this.targetUrl(user));
            } else {
              this.errorMessage = isAr
                ? 'هذا الحساب لا يملك صلاحيات الدخول كمسؤول للمنصة.'
                : 'This account does not have platform administrator access.';
              this.auth.clearAuthenticationState(true);
            }
          },
          error: (err) => {
            this.loading = false;
            this.errorMessage = err?.error?.message || (isAr ? 'فشل التحقق من صلاحيات الحساب.' : 'Failed to verify account permissions.');
          }
        });
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 429) {
          this.errorMessage = isAr
            ? 'تم تجاوز عدد المحاولات الخاطئة. تم إلغاء جلسة التحقق، يرجى تسجيل الدخول مجدداً.'
            : 'Too many failed attempts. Challenge cancelled, please log in again.';
          this.cancelMfa();
        } else if (err?.error?.error_code === 'INVALID_OR_EXPIRED_CHALLENGE') {
          this.errorMessage = isAr
            ? 'انتهت صلاحية جلسة التحقق. يرجى تسجيل الدخول مجدداً.'
            : 'MFA challenge expired. Please log in again.';
          this.cancelMfa();
        } else {
          this.errorMessage = err?.error?.message || (isAr ? 'رمز التحقق غير صحيح.' : 'Invalid verification code.');
        }
      }
    });
  }
}
